import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dices, Plus, Minus, X, History, Zap } from "lucide-react";
import { toast } from "sonner";
import { pushLocalRoll, loadLocalRolls } from "@/lib/storage";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STAT_KEYS } from "@/lib/character";

const DICE = [4, 6, 8, 10, 12, 20, 100];

function rollDie(sides) {
  return Math.floor(Math.random() * sides) + 1;
}

export default function DiceRoller({ character }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [modifier, setModifier] = useState(0);
  const [dr, setDr] = useState(12);
  const [drOn, setDrOn] = useState(false);
  const [advantage, setAdvantage] = useState("normal"); // normal | adv | dis
  const [rolls, setRolls] = useState(loadLocalRolls());
  const [lastResult, setLastResult] = useState(null);
  const [rolling, setRolling] = useState(false);
  const stateRef = useRef({ modifier, advantage, dr, drOn });
  useEffect(() => { stateRef.current = { modifier, advantage, dr, drOn }; }, [modifier, advantage, dr, drOn]);

  const commitRoll = async ({ label, dice, results, mod, drApplied }) => {
    const total = results.reduce((a, b) => a + b, 0) + (mod || 0);
    const outcome = drApplied ? (total >= drApplied ? "PASS" : "FAIL") : null;
    const entry = {
      id: crypto.randomUUID(),
      label,
      dice,
      modifier: mod || 0,
      rolls: results,
      total,
      dr: drApplied || null,
      outcome,
      created_at: new Date().toISOString(),
    };
    setLastResult(entry);
    setRolls((prev) => [entry, ...prev].slice(0, 100));
    pushLocalRoll(entry);
    if (user) {
      try {
        await api.post("/rolls", { label, dice, modifier: mod || 0, rolls: results, total });
      } catch (_e) { /* cloud sync optional */ }
    }
    const desc = `[${results.join(", ")}]${mod ? ` ${mod > 0 ? "+" : ""}${mod}` : ""}${drApplied ? ` vs DR${drApplied}` : ""}`;
    if (outcome === "PASS") toast.success(`${label} → ${total} · PASS`, { description: desc });
    else if (outcome === "FAIL") toast.error(`${label} → ${total} · FAIL`, { description: desc });
    else toast.success(`${label} → ${total}`, { description: desc });
  };

  const doRoll = async ({ sides, count = 1, label, mod = modifier, useDR = drOn && sides === 20 }) => {
    setRolling(true);
    let results;
    if (advantage === "adv" && count === 1) {
      const a = rollDie(sides), b = rollDie(sides);
      results = [Math.max(a, b)];
      label = `${label} [ADV ${a},${b}]`;
    } else if (advantage === "dis" && count === 1) {
      const a = rollDie(sides), b = rollDie(sides);
      results = [Math.min(a, b)];
      label = `${label} [DIS ${a},${b}]`;
    } else {
      results = Array.from({ length: count }, () => rollDie(sides));
    }
    setTimeout(async () => {
      await commitRoll({ label, dice: `${count}d${sides}`, results, mod, drApplied: useDR ? dr : null });
      setRolling(false);
    }, 420);
  };

  const rollD66 = async () => {
    setRolling(true);
    const a = rollDie(6), b = rollDie(6);
    const combined = a * 10 + b;
    setTimeout(async () => {
      await commitRoll({ label: "D66", dice: "d66", results: [combined], mod: 0 });
      setRolling(false);
    }, 420);
  };

  const rollStat = (statKey, statLabel) => {
    const mod = character ? (character[statKey] || 0) : 0;
    // CY_BORG check: d20 + stat
    doRoll({ sides: 20, count: 1, label: `${statLabel} CHECK`, mod });
  };

  // Listen for external roll requests (e.g. Weapon Quick-Attack)
  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail || {};
      setOpen(true);
      const label = detail.label || "STRIKE";
      const sides = detail.sides;
      const count = detail.count || 1;
      const mod = detail.mod ?? 0;
      if (!sides) return;
      // Bypass adv/dis when firing an auto-attack — user hasn't chosen anything.
      setRolling(true);
      const results = Array.from({ length: count }, () => rollDie(sides));
      setTimeout(async () => {
        await commitRoll({ label, dice: `${count}d${sides}`, results, mod });
        setRolling(false);
      }, 420);
    };
    window.addEventListener("cyborg:roll", handler);
    return () => window.removeEventListener("cyborg:roll", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <>
      {/* Floating toggle */}
      <button
        aria-label="Open dice roller"
        data-testid="dice-toggle-btn"
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-4 right-4 z-50 w-14 h-14 border-2 border-[color:var(--accent-yellow)] bg-[color:var(--bg-void)] text-[color:var(--accent-yellow)] hover:bg-[color:var(--accent-yellow)] hover:text-[color:var(--bg-void)] transition-colors flex items-center justify-center shadow-[4px_4px_0_0_var(--accent-yellow)]"
      >
        <Dices size={26} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ x: 400, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 400, opacity: 0 }}
            transition={{ type: "spring", damping: 22 }}
            className="fixed bottom-4 right-4 z-50 w-[92vw] sm:w-[420px] max-h-[85vh] overflow-y-auto panel panel-yellow p-4 sm:p-5"
            data-testid="dice-roller-panel"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="font-display text-xl font-black uppercase tracking-tighter text-[color:var(--accent-yellow)]">
                DICE//TERMINAL
              </div>
              <button onClick={() => setOpen(false)} aria-label="Close" data-testid="dice-close-btn" className="p-2 hover:bg-[color:var(--accent-magenta)] hover:text-[color:var(--bg-void)]">
                <X size={18} />
              </button>
            </div>

            {/* Result display */}
            <div className={`border-2 ${lastResult?.outcome === "PASS" ? "border-[color:var(--accent-toxic)]" : lastResult?.outcome === "FAIL" ? "border-[color:var(--accent-magenta)]" : "border-[color:var(--accent-cyan)]"} p-3 mb-3 min-h-[80px] flex flex-col justify-center ${lastResult ? "result-flash" : ""}`} data-testid="dice-result">
              {lastResult ? (
                <>
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="text-xs font-mono uppercase tracking-widest text-[color:var(--text-secondary)] truncate">{lastResult.label}</div>
                    {lastResult.outcome && (
                      <div
                        data-testid="dice-outcome"
                        className={`text-xs font-display font-black tracking-widest ${lastResult.outcome === "PASS" ? "text-[color:var(--accent-toxic)]" : "text-[color:var(--accent-magenta)]"}`}
                      >
                        {lastResult.outcome}
                      </div>
                    )}
                  </div>
                  <div className={`font-display font-black text-5xl leading-none text-[color:var(--accent-yellow)] ${rolling ? "dice-rolling" : ""}`}>
                    {lastResult.total}
                  </div>
                  <div className="text-xs font-mono text-[color:var(--text-muted)] mt-1">
                    [{lastResult.rolls.join(", ")}]{lastResult.modifier ? ` ${lastResult.modifier > 0 ? "+" : ""}${lastResult.modifier}` : ""}{lastResult.dr ? ` vs DR${lastResult.dr}` : ""}
                  </div>
                </>
              ) : (
                <div className="text-xs font-mono uppercase text-[color:var(--text-muted)]">Awaiting roll…</div>
              )}
            </div>

            {/* DR selector — applies to d20 rolls */}
            <div className="mb-3 border-2 border-[color:var(--border-hard)] p-2" data-testid="dr-panel">
              <div className="flex items-center justify-between mb-2">
                <button
                  data-testid="dr-toggle"
                  onClick={() => setDrOn((v) => !v)}
                  className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 border-2 ${drOn ? "bg-[color:var(--accent-toxic)] text-[color:var(--bg-void)] border-[color:var(--accent-toxic)]" : "text-[color:var(--text-secondary)] border-[color:var(--border-hard)] hover:border-[color:var(--accent-toxic)]"}`}
                >
                  {drOn ? "DR ON" : "DR OFF"}
                </button>
                <div className="flex items-center gap-1">
                  <button aria-label="Decrement DR" data-testid="dr-dec" onClick={() => setDr((v) => Math.max(1, v - 1))} className="p-1 hover:text-[color:var(--accent-magenta)]"><Minus size={12} /></button>
                  <input
                    data-testid="dr-input"
                    type="number"
                    value={dr}
                    onChange={(e) => setDr(Math.max(1, parseInt(e.target.value || "0", 10)))}
                    className="cy-input border-0 text-center w-14"
                    style={{ minHeight: 28 }}
                  />
                  <button aria-label="Increment DR" data-testid="dr-inc" onClick={() => setDr((v) => v + 1)} className="p-1 hover:text-[color:var(--accent-cyan)]"><Plus size={12} /></button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1" data-testid="dr-presets">
                {[
                  { v: 8, l: "EASY 8" },
                  { v: 10, l: "DR10" },
                  { v: 12, l: "DR12" },
                  { v: 14, l: "HARD 14" },
                  { v: 16, l: "DR16" },
                  { v: 18, l: "INSANE 18" },
                ].map((p) => (
                  <button
                    key={p.v}
                    data-testid={`dr-preset-${p.v}`}
                    onClick={() => { setDr(p.v); setDrOn(true); }}
                    className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 border-2 ${dr === p.v && drOn ? "bg-[color:var(--accent-yellow)] text-[color:var(--bg-void)] border-[color:var(--accent-yellow)]" : "text-[color:var(--text-secondary)] border-[color:var(--border-hard)] hover:border-[color:var(--accent-yellow)]"}`}
                  >
                    {p.l}
                  </button>
                ))}
              </div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-[color:var(--text-muted)] mt-1">
                {drOn ? "Applied on any d20 roll (checks + stat rolls)" : "Toggle on to grade d20 rolls PASS/FAIL"}
              </div>
            </div>

            {/* Basic dice */}
            <div className="grid grid-cols-4 gap-2 mb-3">
              {DICE.map((s) => (
                <button
                  key={s}
                  data-testid={`roll-d${s}-btn`}
                  onClick={() => doRoll({ sides: s, count: 1, label: `D${s}` })}
                  className="cy-btn text-sm px-2 py-2"
                >
                  D{s}
                </button>
              ))}
              <button data-testid="roll-d66-btn" onClick={rollD66} className="cy-btn cy-btn-magenta text-sm px-2 py-2">D66</button>
            </div>

            {/* Modifier + Adv/Dis */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="flex items-center border-2 border-[color:var(--border-hard)]">
                <button aria-label="Decrement modifier" data-testid="mod-decrement" onClick={() => setModifier((m) => m - 1)} className="p-2 hover:bg-[color:var(--accent-magenta)] hover:text-[color:var(--bg-void)]"><Minus size={14} /></button>
                <input
                  data-testid="modifier-input"
                  type="number"
                  value={modifier}
                  onChange={(e) => setModifier(parseInt(e.target.value || "0", 10))}
                  className="cy-input border-0 text-center"
                  style={{ minHeight: 40 }}
                />
                <button aria-label="Increment modifier" data-testid="mod-increment" onClick={() => setModifier((m) => m + 1)} className="p-2 hover:bg-[color:var(--accent-cyan)] hover:text-[color:var(--bg-void)]"><Plus size={14} /></button>
              </div>
              <div className="flex border-2 border-[color:var(--border-hard)]">
                {[
                  { k: "dis", l: "DIS" },
                  { k: "normal", l: "NORM" },
                  { k: "adv", l: "ADV" },
                ].map((o) => (
                  <button
                    key={o.k}
                    data-testid={`adv-${o.k}`}
                    onClick={() => setAdvantage(o.k)}
                    className={`flex-1 text-xs font-bold uppercase tracking-widest py-2 ${advantage === o.k ? "bg-[color:var(--accent-yellow)] text-[color:var(--bg-void)]" : "text-[color:var(--text-secondary)] hover:text-[color:var(--accent-yellow)]"}`}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
            </div>

            {/* Stat-linked rolls */}
            {character && (
              <div className="mb-3">
                <div className="text-xs font-mono uppercase tracking-widest text-[color:var(--text-secondary)] mb-2 flex items-center gap-1">
                  <Zap size={12} className="text-[color:var(--accent-magenta)]" /> STAT CHECKS ({character.name})
                </div>
                <div className="grid grid-cols-5 gap-1">
                  {STAT_KEYS.map((s) => (
                    <button
                      key={s.key}
                      data-testid={`stat-roll-${s.key}`}
                      onClick={() => rollStat(s.key, s.label)}
                      className="cy-btn cy-btn-cyan text-xs px-1 py-2 flex-col"
                      title={`Roll ${s.label} (d20 ${character[s.key] >= 0 ? "+" : ""}${character[s.key] || 0})`}
                    >
                      <span className="text-[10px]">{s.abbr}</span>
                      <span className="text-sm text-[color:var(--accent-yellow)]">{(character[s.key] || 0) >= 0 ? "+" : ""}{character[s.key] || 0}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* History */}
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-[color:var(--text-secondary)] mb-2 flex items-center gap-1">
                <History size={12} /> ROLL HISTORY
              </div>
              <div className="max-h-[200px] overflow-y-auto border-2 border-[color:var(--border-hard)]" data-testid="roll-history">
                {rolls.length === 0 && <div className="p-3 text-xs font-mono text-[color:var(--text-muted)]">no rolls yet.</div>}
                {rolls.map((r) => (
                  <div key={r.id} className="p-2 border-b border-[color:var(--border-hard)] flex items-center justify-between text-xs font-mono">
                    <div>
                      <div className="text-[color:var(--accent-cyan)]">
                        {r.label}
                        {r.outcome && (
                          <span className={`ml-2 font-bold ${r.outcome === "PASS" ? "text-[color:var(--accent-toxic)]" : "text-[color:var(--accent-magenta)]"}`}>{r.outcome}</span>
                        )}
                      </div>
                      <div className="text-[color:var(--text-muted)]">{r.dice} [{r.rolls.join(",")}]{r.modifier ? ` ${r.modifier > 0 ? "+" : ""}${r.modifier}` : ""}{r.dr ? ` vs DR${r.dr}` : ""}</div>
                    </div>
                    <div className="text-[color:var(--accent-yellow)] text-lg font-black">{r.total}</div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
